import Box from "@mui/material/Box";
import { Field, Form, Formik, FormikProps, getIn } from "formik";
import { useTranslation } from "next-i18next";
import Button from "@ds/components/Button";
import FormControl from "@ds/components/FormControl";
import AutocompleteBase from "@ds/components/AutocompleteBase/AutocompleteBase";
import Text from "@ds/components/Text";
import FieldInput from "@ds/fields/input-text";
import FieldDropdown from "@ds/fields/dropdown";
import FieldTextArea from "@ds/fields/text-area";
import useAction from "./hooks/useAction";
import useStyles from "./styles";
import type { TContainerForm, TInterestedForm } from "./type";
import validateForm from "./validation";
import { Checkbox } from "@mui/material";

const FormInterest: React.FC<TContainerForm> = () => {
  const classes = useStyles();
  const { t } = useTranslation("common");
  const {
    initialValueForm,
    dialCode,
    industryData,
    companySizeData,
    cityData,
    onCityChange,
    onSubmitForm,
    isChecked,
    onAgreeTermChange,
    language,
  } = useAction();
  return (
    <Formik
      initialValues={initialValueForm}
      onSubmit={onSubmitForm}
      enableReinitialize
      validationSchema={validateForm(t)}
    >
      {({ values, dirty, isValid, isSubmitting }: FormikProps<Partial<TInterestedForm>>) => {
        return (
          <Box pt={2}>
            <Form className={classes.container}>
              <Box my={2}>
                <Field
                  label={t("interest.fullName.label")}
                  name="fullName"
                  component={FieldInput}
                  placeholder={t("interest.fullName.placeHolder")}
                />
              </Box>
              <Box my={2}>
                <Field
                  label={t("interest.phoneNumber.label")}
                  name="phoneNumber"
                  component={FieldInput}
                  type="number"
                  placeholder={t("interest.phoneNumber.placeHolder")}
                  startAdornment={
                    <Text color="general-mid" variant="body1" sx={{ paddingRight: 8 }}>
                      {dialCode ? dialCode : ""}
                    </Text>
                  }
                />
              </Box>
              <Box mt={2}>
                <Field
                  label={t("interest.workEmail.label")}
                  type="email"
                  name="email"
                  component={FieldInput}
                  placeholder={t("interest.workEmail.placeHolder")}
                  hint={t("interest.workEmail.hint")}
                />
              </Box>
              <Box my={2}>
                <Field
                  label={t("interest.businessInstName.label")}
                  name="companyName"
                  component={FieldInput}
                  placeholder={t("interest.businessInstName.placeHolder")}
                />
              </Box>
              <Box my={2}>
                <Field
                  width="100%"
                  label={t("interest.industry.label")}
                  name="industry"
                  component={FieldDropdown}
                  placeholder={t("interest.industry.placeHolder")}
                  options={industryData}
                  value={values.industry}
                />
              </Box>
              <Box my={2}>
                <Field
                  width="100%"
                  label={t("interest.companySize.label")}
                  name="companySize"
                  component={FieldDropdown}
                  placeholder={t("interest.companySize.placeHolder")}
                  options={companySizeData}
                  value={values.companySize}
                />
              </Box>
              <Box my={2}>
                <Field
                  name="city"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  children={({ field, form, hint }: any) => {
                    const { value } = field;
                    const { touched, errors, setFieldValue } = form;
                    const isError = getIn(touched, field.name) && getIn(errors, field.name);
                    return (
                      <FormControl
                        label={t("interest.city.label")}
                        error={isError}
                        form={form}
                        field={field}
                        hint={hint}
                      >
                        <AutocompleteBase
                          width="100%"
                          value={value}
                          placeholder={t("interest.city.placeHolder")}
                          options={cityData}
                          onInputChange={(value) => {
                            setFieldValue([field.name], value);
                            onCityChange(value);
                          }}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Box>
              <Box mt={2}>
                <Field
                  label={t("interest.message.label")}
                  name="message"
                  maxlength={500}
                  component={FieldTextArea}
                  placeholder={t("interest.message.placeHolder")}
                  info={t("interest.message.info")}
                />
              </Box>
              <Box mt={2} mb={4} sx={{ display: "flex", alignItems: "flex-start" }}>
                <Checkbox
                  checked={isChecked}
                  size="small"
                  onChange={onAgreeTermChange}
                  style={{ marginTop: "8px" }}
                />
                <Text component="p" color="general-mid" variant="body2">
                  {t("interest.termConditionMessage")}
                  <a
                    href="/syarat-dan-ketentuan"
                    target="_blank"
                    style={{ color: "#C72037", textDecoration: "underline" }}
                  >
                    {t("footer.menu.terms")}
                  </a>
                  {language === "id" ? " dan " : " and "}
                  <a
                    href="/kebijakan-privasi"
                    target="_blank"
                    style={{ color: "#C72037", textDecoration: "underline" }}
                  >
                    {t("footer.menu.privacy")}
                  </a>
                  {t("interest.termConditionMessageSecond")}
                </Text>
              </Box>
              <Button
                type="submit"
                size="large"
                width="100%"
                loading={isSubmitting}
                disabled={!(dirty && isValid && isChecked) || isSubmitting}
                sx={{ padding: "12px" }}
              >
                {t("interest.submit")}
              </Button>
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
};

export default FormInterest;
